import { postApi } from "./api";
import { currentAuthGeneration, requireCurrentAuth } from "./auth";
import { confirmCollectionRequest, pendingCollection, retainCollectionRequest } from "./collectionRequests";

// The authenticated API decodes and rewrites images before publishing them on
// an isolated receipt origin. Arbitrary public S3 PUT capabilities are not issued.
export async function uploadReceipt(_debtId: string, file: File): Promise<string> {
  const generation = currentAuthGeneration();
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Choose a JPEG, PNG or WebP image.");
  if (!file.size || file.size > 3 * 1024 * 1024) throw new Error("Receipt images must be no larger than 3 MB.");
  const bytes = new Uint8Array(await file.arrayBuffer());
  requireCurrentAuth(generation);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  const r = await postApi("/api/receipts", { image: btoa(binary), contentType: file.type });
  requireCurrentAuth(generation);
  const { publicUrl } = await r.json() as { publicUrl: string };
  requireCurrentAuth(generation);
  return publicUrl;
}

// Record a payment against a debt's plan. `receipt` is a URL (uploaded image or
// pasted link), optional. `account` is the optional cash account: the server
// debits it for payable debts and credits it for receivable collections. A
// collection always creates its actual income row, even without an account.
export async function logPayment(debtId: string, amount: number, date: string, receipt?: string, account?: string, requestId?: string): Promise<void> {
  const generation = currentAuthGeneration();
  const response = await postApi("/api/finances", { action: "loan_payment", debtId, amount, date, ...(receipt ? { receipt } : {}), ...(account ? { account } : {}), ...(requestId ? { requestId } : {}) });
  requireCurrentAuth(generation);
  const result = await response.json() as { vaultSyncPending?: boolean };
  requireCurrentAuth(generation);
  if (result.vaultSyncPending) throw new Error("Payment recorded. Vault sync is pending. Retry this submission to sync its note; it will not record a second payment.");
}

// Keep the whole submission on its original session. A normal token renewal
// preserves the generation; explicit logout or session expiry cancels it.
export async function submitDebtPayment(input: {
  debtId: string; amount: number; date: string; file?: File; account?: string;
}, generation = currentAuthGeneration()): Promise<void> {
  requireCurrentAuth(generation);
  let receipt = pendingCollection(input.debtId)?.receipt;
  if (input.file && !receipt) receipt = await uploadReceipt(input.debtId, input.file);
  requireCurrentAuth(generation);
  const request = retainCollectionRequest({ debtId: input.debtId, amount: input.amount, date: input.date, receipt, account: input.account });
  await logPayment(input.debtId, input.amount, input.date, receipt, input.account, request.requestId);
  requireCurrentAuth(generation);
  confirmCollectionRequest(request);
}
